<template>
    <b-modal v-if="survey" v-model="showSatisfactionSurvey" id="questionnaireModal" size="xl" centered  no-close-on-backdrop no-close-on-esc
             dialog-class="mw-50" content-class="overflow-hidden" class="p-2 p-md-4 mx-2 mx-md-auto">
        <template #modal-header="{ close }">
            <h5 class="mb-0 d-flex align-items-center">
                <b-icon icon="clipboard-check" class="mr-2" />
                {{ survey.Title }}
            </h5>
        </template>

        <div class="d-flex justify-content-center align-items-center px-3 mb-3">
            <b-img :src="require('../../assets/images/Satisfaction_Survey_logo.jpg')" fluid
                   alt="Satisfaction Survey Logo"></b-img>
        </div>

        <b-card no-body class="pl-2 pr-2 pb-0 pt-0">

            <b-form-group :label="survey.Header" label-cols="12"
                          label-class="font-weight-bold pt-1 mb-2 d-flex align-items-center justify-start px-3" class="mb-0">

                <QuestionSurvey v-for="question in survey.Questions" :key="question.Id" :question="question.Label"
                                :question-ref="question.Id" :hasRemark="question.HasRemark" @input="updateQuestionValue" />

            </b-form-group>

        </b-card>

        <template #modal-footer>
            <div class="row w-100 align-items-center no-gutters">
                <!-- Texte du footer -->
                <b-col cols="12" md="9" class="text-left">
                    <h6 class="font-weight-bold pt-1 mb-2" style="line-height: 18px;" v-html="survey.Footer"></h6>
                </b-col>
                <!-- Bouton pour valider le formulaire -->
                <b-col cols="12" md="3" class="text-right">
                    <b-button @click="saveSatisfaction" variant="secondary" pill :disabled="!isValid">
                        <font-awesome-icon icon="save" />
                        {{ $t('Validate') }}
                    </b-button>
                </b-col>

            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
    import { Component, Vue } from "vue-property-decorator";
    import locales from "./locales/SatisfactionSurvey.json";
    import globalLocales from "../../locales/translation.json"
    import axios from "axios";
    import QuestionSurvey from "./SatisfactionSurvey/QuestionSurvey.vue";
    import { API_URL } from "@/App.settings";
    import Dictionary from "@/types/Dictionary";

    @Component({
        components: { QuestionSurvey },
        i18n: { messages: locales, sharedMessages: globalLocales },
    })
    export default class SatisfactionSurvey extends Vue {

        /**  État de l'affichage de la modal du questionnaire */
        private showSatisfactionSurvey: boolean = false;

        /**  Données du questionnaire */
        private survey?: Survey = null;

        /**  Données de réponse formulaire de satisfaction */
        private postedSurvey: SubmitedSatisfactionSurvey = new SubmitedSatisfactionSurvey();

        /** Validité du formulaire */
        private isValid: boolean = false;

        /**
        * Le composant enfant QuestionSurvey.vue émet un objet : { replyValue, remark }
        * On met à jour ou on ajoute la question correspondante dans postedSurvey.Replies.
        */
        private updateQuestionValue(data: { questionRef: number, replyValue?: number, remark: string }) {
            let existing = this.postedSurvey.Replies.find(r => r.questionRef === data.questionRef);

            if (!existing) {
                this.postedSurvey.Replies.push(
                    new QuestionReply(data.questionRef, data.replyValue, data.remark)
                );
            }
            else {
                existing.replyValue = data.replyValue;
                existing.remark = data.remark;
            }

            this.validateForm();
        }

        /** Fonction pour valider que toutes les questions ont une réponse */
        private validateForm(): void {
            if (!this.survey?.Questions || this.survey.Questions.length === 0) {
                this.isValid = false;
                return;
            }

            /**  Vérifie que chaque question a une réponse non nulle */
            let allAnswered = this.survey.Questions.every(q => {
                let reply = this.postedSurvey.Replies.find(r => r.questionRef === q.Id);
                return reply && reply.replyValue !== null && reply.replyValue !== undefined;
            });

            /** Vérification des remarques si trop longue */
            let hasInvalidRemarks = this.postedSurvey.Replies.some(r => {
                /** Si trop longue (> 256 caractères) */
                if (r.remark && r.remark.length > 256) return true;

            });
            /** Le formulaire est valide que si toutes les questions sont répondu et que les remarques ne dépassent pas 256 caractères */
            this.isValid = allAnswered && !hasInvalidRemarks;
        }

        /** Fonction pour soumettre le questionnaire de satisfaction */
        private async saveSatisfaction() {
            if (!this.isValid) {
                this.$bvToast.toast(this.$t('PleaseFillForm').toString(), {
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
                return;
            }
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["Data"] = JSON.stringify({
                SurveyId: this.postedSurvey.SurveyId,
                Replies: this.postedSurvey.Replies
            });

            await axios.post(`${API_URL}Satisfaction/Save`, args)
                .then((response) => {
                    if (response && response.data) {
                        this.$bvToast.toast(this.$t('ThanksParticipation').toString(), {	// Merci pour votre participation !
                            autoHideDelay: 3000,
                            variant: "success",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                        });
                    }
                    this.showSatisfactionSurvey = false;
                })
                .catch((err) => {
                    console.error(err);
                    this.$bvToast.toast(this.$t('AnErrorOccured').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                });
        }

        /**  Récupération automatique du questionnaire à la montée du composant */
        async mounted() {
            /** Fonction pour récupérer le questionnaire de satisfaction*/
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            await axios.post<Survey>(`${API_URL}Satisfaction/GetCurrentSurvey`, args)
                .then((response) => {
                    if (response && response.data) {
                        this.survey = response.data;
                        this.postedSurvey.SurveyId = this.survey.Id;

                        this.showSatisfactionSurvey = true;
                    }
                    else
                        this.showSatisfactionSurvey = false;
                })
                .catch((err) => {
                    console.error(err);
                    this.$bvToast.toast(this.$t('AnErrorOccured').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                    this.showSatisfactionSurvey = false;
                });
        }
    }

    /**  Classe pour le questionnaire */
    class Survey {
        Id: number;
        Title: string;
        Header: string;
        Footer: string;
        Questions: Question[];
    }

    /**  Classe pour les questions */
    class Question {
        Id: number;
        Label: string;
        HasRemark: boolean;
    }

    /**  Classe pour les réponses au formulaire */
    class SubmitedSatisfactionSurvey {
        SurveyId: number;

        /** Liste des réponses de questions */
        Replies: QuestionReply[];

        constructor() {
            this.SurveyId = 0;
            this.Replies = [];
        }
    }

    /**  Classe pour la structure d'une reponse dédiée à la question */
    class QuestionReply {
        questionRef: number;
        replyValue: number | null;
        remark: string | null;

        constructor(questionRef: number, replyValue: number | null, remark: string | null = "") {
            this.questionRef = questionRef;
            this.replyValue = replyValue;
            this.remark = remark;
        }
    }
</script>

<style scoped></style>
